import React, { useState, useEffect, useRef } from 'react';
import logo from '../assets/logo.jpg';


interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
}

interface BotResponse {
  text: string;
  quickReplies?: string[];
}

const MasaHillsChatbot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (!initialized.current) {
      showInitialMessages();
      initialized.current = true;
    }
  }, []);

  const addMessage = (sender: 'user' | 'bot', text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      sender,
      text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const showInitialMessages = () => {
    const disclaimerText = `<p class="font-bold text-sm mb-2 text-blue-900">Important Disclaimer:</p><p class="text-sm">Hello! I am an AI assistant for Masa Hills Health Services. I can provide general information about our services and policies. <strong class="font-bold">Please do not share any personal, medical, or Protected Health Information (PHI).</strong> This is not a secure channel for PHI. In a medical emergency, please dial 911.</p>`;
    
    addMessage('bot', disclaimerText);
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('bot', "Welcome! I'm here to help you learn about our services. What can I help you with today?");
      setQuickReplies(['Our Services', 'Job Opportunities', 'Contact Us']);
    }, 1500);
  };

  const getBotResponse = (message: string): BotResponse => {
    const lowerCaseMessage = message.toLowerCase();

    // PHI Check
    const phiPatterns = [/\b\d{3}[- ]?\d{2}[- ]?\d{4}\b/]; // SSN
    const medicalTerms = ['alzheimer', 'diabetes', 'cancer', 'bathing', 'dressing', 'toileting', 'dementia', 'pain', 'medication', 'doctor', 'physician', 'hospital', 'diagnosis'];
    const namePattern = /\b([A-Z][a-z]+)\s+([A-Z][a-z]+)\b/; // Simple Firstname Lastname check
    
    if (phiPatterns.some(p => p.test(lowerCaseMessage)) || (namePattern.test(message) && medicalTerms.some(term => lowerCaseMessage.includes(term)))) {
      return {
        text: "I'm sorry, but I cannot process that request. To protect your privacy, please do not share any personal or medical details in this chat.<br><br>For specific needs, please call our office directly at <strong>(469) 573-0176</strong> to speak with a representative in a confidential manner.",
        quickReplies: ['Main Menu']
      };
    }

    // Response mapping (updated and expanded)
    const responses: { [key: string]: BotResponse } = {
      'default': {
        text: "I'm not sure how to answer that. For complex questions or specific needs, it's best to call our office at <strong>(469) 573-0176</strong>. Otherwise, I can help with one of the topics below.",
        quickReplies: ['Our Services', 'Job Opportunities', 'Contact Us']
      },
      'care_inquiry': {
        text: "It sounds like you're looking for care for yourself or a loved one. I understand, and we're here to help.<br><br>To best assist you, the next step is a confidential conversation with our care team to understand your unique needs. Please call us at <strong>(469) 573-0176</strong> for a free consultation.<br><br>In the meantime, what general information can I provide?",
        quickReplies: ['Our Services', 'Contact Us', 'How to Become a Client']
      },
      'help': {
        text: "Of course, I can help. What topic are you interested in?",
        quickReplies: ['Our Services', 'Job Opportunities', 'Contact Us']
      },
      'main menu': {
        text: "How can I help you?",
        quickReplies: ['Our Services', 'Job Opportunities', 'Contact Us']
      },
      'hello': {
        text: "Hello! Thank you for reaching out. How can I assist you with general information about Masa Hills Health Services today?",
        quickReplies: ['Our Services', 'Job Opportunities', 'Contact Us']
      },
      'services': {
        text: `Masa Hills Health Services offers two main types of non-medical care:<br><br><strong>1. Companionship Services:</strong> Focused on providing social interaction and help with daily household tasks.<br><br><strong>2. Personal Assistance Services (PAS):</strong> Provides all companionship duties plus hands-on help with personal care.`,
        quickReplies: ['More on Companionship', 'More on PAS', 'What services are NOT provided?']
      },
      'companionship': {
        text: `Our <strong>Companionship Services</strong> include:<br><ul><li class='ml-4'>• Friendly conversation and social activities</li><li class='ml-4'>• Meal preparation and planning</li><li class='ml-4'>• Light housekeeping (dusting, vacuuming, laundry)</li><li class='ml-4'>• Transportation for errands and appointments</li><li class='ml-4'>• Pet care assistance</li></ul>`,
        quickReplies: ['Tell me about PAS', 'Main Menu']
      },
      'pas': {
        text: `Our <strong>Personal Assistance Services (PAS)</strong> include all Companionship duties, plus hands-on assistance with Activities of Daily Living (ADLs) such as:<br><ul><li class='ml-4'>• Bathing and showering</li><li class='ml-4'>• Dressing and grooming</li><li class='ml-4'>• Toileting and incontinence care</li><li class='ml-4'>• Transferring and mobility assistance</li><li class='ml-4'>• Medication reminders</li></ul>`,
        quickReplies: ['What services are NOT provided?', 'Main Menu']
      },
      'not provide': {
        text: `For the safety and compliance of our clients, we do not provide certain medical services. This includes:<br><ul><li class='ml-4'>• Changing sterile dressings</li><li class='ml-4'>• Intravenous (IV) therapy</li><li class='ml-4'>• Administering injections (like insulin)</li><li class='ml-4'>• Any service requiring a doctor's direct oversight.</li></ul><br>Our focus is on providing excellent non-medical care.`,
        quickReplies: ['Our Services', 'Contact Us']
      },
      'job': {
        text: `We are always looking for compassionate and responsible individuals to join our team!<br><br>The first step is to complete our online application. Our hiring process includes an interview, reference checks, and a criminal history check. You can call our office at <strong>(469) 573-0176</strong> for more details.`,
        quickReplies: ['What is the dress code?', 'Main Menu']
      },
      'dress code': {
        text: "Our caregivers are expected to maintain a professional appearance. Scrubs are generally required, but this can vary by client. Inappropriate attire includes jeans, t-shirts, and open-toed shoes. For full details, please speak with our hiring manager.",
        quickReplies: ['Job Opportunities', 'Main Menu']
      },
      'become a client': {
        text: `We would be honored to provide care. The first step is to call our office at <strong>(469) 573-0176</strong> for an initial inquiry.<br><br>We will then schedule a confidential client assessment to understand your needs and create a personalized Individual Service Plan to ensure we provide the best possible care.`,
        quickReplies: ['What are your hours?', 'Main Menu']
      },
      'complaint': {
        text: `We take all feedback seriously. If you have a complaint or concern, you can report it to our office 24/7 by calling <strong>(469) 573-0176</strong>.<br><br>You also have the right to contact the Texas Department of Health and Human Services (HHS) at <strong>1-800-458-9858</strong>.`,
        quickReplies: ['Contact Us', 'Main Menu']
      },
      'contact': {
        text: `You can reach us at:<br><strong>Phone:</strong> (469) 573-0176 (Available 24/7)<br><strong>Office Address:</strong> 3410 Texas Dr, Sachse, TX 75048<br><strong>Office Hours:</strong> Monday - Monday, 7:00 AM to 5:00 PM.`,
        quickReplies: ['Our Services', 'Job Opportunities']
      },
      'privacy': {
        text: `We adhere strictly to HIPAA regulations and Texas law to protect client privacy. All client information is confidential. For any matter involving Protected Health Information (PHI), please call our office directly at <strong>(469) 573-0176</strong>.`,
        quickReplies: ['Our Services', 'Main Menu']
      },
      'area': {
        text: `Masa Hills Health Services provides care across a wide service area in Texas, including the regions of Abilene, Austin, Beaumont, Corpus Christi, Dallas/Fort Worth, El Paso, Houston, Lubbock, San Antonio, Tyler, and many surrounding counties.<br><br>For specific inquiries about your location, please call our office.`,
        quickReplies: ['Contact Us', 'Main Menu']
      },
      'thank': {
        text: "You're very welcome! Is there anything else I can help you with?",
        quickReplies: ['Main Menu']
      }
    };

    // --- Keyword matching logic (updated) ---
    const careInquiryKeywords = ['care', 'mom', 'dad', 'parent', 'grandma', 'grandpa', 'husband', 'wife', 'senior', 'loved one', 'assistance for'];
    if (careInquiryKeywords.some(keyword => lowerCaseMessage.includes(keyword))) {
      return responses.care_inquiry;
    }

    if (lowerCaseMessage.includes('hello') || lowerCaseMessage.includes('hi')) return responses.hello;
    if (lowerCaseMessage.includes('help')) return responses.help;
    if (lowerCaseMessage.includes('service') || lowerCaseMessage.includes('offer')) return responses.services;
    if (lowerCaseMessage.includes('companionship')) return responses.companionship;
    if (lowerCaseMessage.includes('pas') || lowerCaseMessage.includes('personal assistance')) return responses.pas;
    if (lowerCaseMessage.includes('not provide') || lowerCaseMessage.includes("don't do")) return responses['not provide'];
    if (lowerCaseMessage.includes('apply') || lowerCaseMessage.includes('job') || lowerCaseMessage.includes('hiring') || lowerCaseMessage.includes('career')) return responses.job;
    if (lowerCaseMessage.includes('dress code')) return responses['dress code'];
    if (lowerCaseMessage.includes('become a client') || lowerCaseMessage.includes('sign up')) return responses['become a client'];
    if (lowerCaseMessage.includes('complaint') || lowerCaseMessage.includes('grievance') || lowerCaseMessage.includes('problem')) return responses.complaint;
    if (lowerCaseMessage.includes('contact') || lowerCaseMessage.includes('phone') || lowerCaseMessage.includes('address') || lowerCaseMessage.includes('hours')) return responses.contact;
    if (lowerCaseMessage.includes('hipaa') || lowerCaseMessage.includes('privacy') || lowerCaseMessage.includes('confidential')) return responses.privacy;
    if (lowerCaseMessage.includes('area') || lowerCaseMessage.includes('location') || lowerCaseMessage.includes('serve')) return responses.area;
    if (lowerCaseMessage.includes('thank')) return responses.thank;
    if (lowerCaseMessage.includes('menu')) return responses['main menu'];
    
    return responses.default;
  };

  const handleUserInput = (messageTextOverride?: string) => {
    const messageText = messageTextOverride || inputValue.trim();
    if (messageText === '') return;

    addMessage('user', messageText);
    if (!messageTextOverride) setInputValue('');
    setQuickReplies([]);

    setIsTyping(true);
    setTimeout(() => {
      const response = getBotResponse(messageText);
      setIsTyping(false);
      addMessage('bot', response.text);
      if (response.quickReplies) {
        setQuickReplies(response.quickReplies);
      }
    }, 1200);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleUserInput();
    }
  };

  const TypingIndicator = () => (
    <div className="flex justify-start mb-4">
      <div className="bg-gray-200 text-gray-800 p-3 rounded-xl shadow-sm">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 flex items-center shadow-md z-10 flex-shrink-0">
        <div className="w-12 h-12 mr-4 flex-shrink-0 p-1 bg-white/20 rounded-full flex items-center justify-center">
          <img src={logo} alt="MASA Hills Logo" className="w-10 h-10 object-contain rounded-full" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Masa Hills Helper</h1>
          <p className="text-sm text-blue-100">Your guide to our services</p>
        </div>
      </header>

      {/* Messages Area */}
      <main className="flex-1 p-4 md:p-6 overflow-y-auto bg-gray-50">
        {messages.map((message) => (
          <div key={message.id} className={`flex mb-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`p-3 rounded-xl max-w-md shadow-sm text-sm md:text-base ${
              message.sender === 'user' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-800'
            }`}>
              <div dangerouslySetInnerHTML={{ __html: message.text }} />
            </div>
          </div>
        ))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </main>
      
      {/* Quick Replies Area */}
      {quickReplies.length > 0 && (
        <div className="p-2 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-2 justify-center p-2">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => handleUserInput(reply)}
                className="bg-white text-blue-600 border border-blue-500 px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-50 transition-all duration-200 ease-in-out transform hover:-translate-y-0.5 hover:shadow-lg"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <footer className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          />
          <button
            onClick={() => handleUserInput()}
            className="ml-3 bg-blue-600 text-white rounded-full p-3 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-transform duration-150 ease-in-out transform hover:scale-105"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default MasaHillsChatbot;